/* eslint-disable react-refresh/only-export-components --
 * Provider + useContext live in one file by design. See
 * FrameworkContext.tsx for the rationale.
 */
import { createContext, useContext, type ReactNode } from 'react';

import type { FactorStateKind } from '../framework';
import { useStateSettings, type StateSettingsMap } from './useStateSettings';

/**
 * Holds the loaded state settings (custom SVGs + colours) for use by
 * StateIcon and GlobalStateStyles. Mounted once near the top of the
 * authenticated app so the fetch only fires once per session.
 *
 * Default value is an empty map (no overrides) so components outside
 * the provider still render correctly with defaults.
 */
type ContextValue = {
  map: StateSettingsMap | null;
  customSvgFor: (state: FactorStateKind) => string | null;
};

const StateSettingsContext = createContext<ContextValue>({
  map: null,
  customSvgFor: () => null,
});

export function StateSettingsProvider({ children }: { children: ReactNode }) {
  const settings = useStateSettings();
  const map = settings.kind === 'ok' ? settings.map : null;
  const value: ContextValue = {
    map,
    customSvgFor: (state) => map?.get(state)?.icon_svg ?? null,
  };
  return (
    <StateSettingsContext.Provider value={value}>
      {children}
    </StateSettingsContext.Provider>
  );
}

export function useStateSettingsContext(): ContextValue {
  return useContext(StateSettingsContext);
}
